import { buildPrintHeaderHtml, estilosPrintHeader, getPrintLogoUrl, getPrintEncabezado } from './printBranding';
import { formatFechaSistema } from './fechasSistema';

const formatMoneda = (valor) =>
  parseFloat(valor || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });

export function escHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function esc(text) {
  return escHtml(text);
}

const ESTILOS_LISTADO = `
  body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #333; }
  ${estilosPrintHeader}
  .subtitulo { color: #666; margin-bottom: 16px; font-size: 11px; }
  .filtros-info { margin-bottom: 16px; padding: 10px 12px; background: #f5f5f5; border-radius: 6px; font-size: 11px; }
  .filtros-info p { margin: 3px 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 11px; }
  th { background: #2c3e50; color: white; }
  td.num { text-align: right; }
  .total-row { margin-top: 14px; padding: 10px; background: #e8f4f8; font-weight: bold; text-align: right; font-size: 12px; }
  .pie { margin-top: 12px; font-size: 10px; color: #888; }
  @media print { body { padding: 10px; } }
`;

function abrirVentanaImpresion(html, titulo) {
  const ventana = window.open('', '_blank');
  if (!ventana) {
    throw new Error('No se pudo abrir la ventana de impresión. Verifique que el navegador no bloquee ventanas emergentes.');
  }
  ventana.document.write(html);
  ventana.document.title = titulo || 'Listado';
  ventana.document.close();
}

function imprimirListadoCheques({
  titulo,
  subtitulo,
  columnas,
  filas,
  getValorCelda,
  filtrosHtml = '',
  totalImporte,
  logoUrl,
}) {
  const logo = logoUrl || getPrintLogoUrl();
  const cols = (columnas || []).filter((c) => c.id !== 'comprobantes' || c.incluirComprobantes);
  const headers = cols.map((c) => {
    const align = c.align === 'right' ? ' style="text-align:right"' : '';
    return `<th${align}>${esc(c.label)}</th>`;
  }).join('');

  const bodyRows = (filas || []).map((fila) => {
    const tds = cols.map((c) => {
      const valor = getValorCelda(fila, c.id);
      const align = c.align === 'right' ? ' class="num"' : '';
      return `<td${align}>${valor}</td>`;
    }).join('');
    return `<tr>${tds}</tr>`;
  }).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${esc(titulo)}</title>
      <style>${ESTILOS_LISTADO}</style>
    </head>
    <body>
      ${buildPrintHeaderHtml({
        titulo,
        logoUrl: logo,
        encabezado: getPrintEncabezado(),
      })}
      ${subtitulo ? `<div class="subtitulo">${esc(subtitulo)}</div>` : ''}
      ${filtrosHtml ? `<div class="filtros-info">${filtrosHtml}</div>` : ''}
      <table>
        <thead><tr>${headers}</tr></thead>
        <tbody>
          ${bodyRows || `<tr><td colspan="${cols.length || 1}">Sin registros</td></tr>`}
        </tbody>
      </table>
      ${totalImporte != null ? `<div class="total-row">Total importe: $${formatMoneda(totalImporte)} (${filas?.length || 0} cheque(s))</div>` : ''}
      <div class="pie">Listado generado por Sistema de Abonos</div>
      <script>window.onload = function() { window.print(); };</script>
    </body>
    </html>
  `;

  abrirVentanaImpresion(html, titulo);
}

function valorCeldaCarteraPlain(ch, colId, formatFecha) {
  const fmt = formatFecha || ((f) => (f ? formatFechaSistema(f) : '-'));
  switch (colId) {
    case 'cliente': return ch.cliente_razon_social || '-';
    case 'numero': return ch.numero || '-';
    case 'banco': return ch.cuenta_banco_nombre || '-';
    case 'caja': return ch.caja_ubicacion_nombre || '-';
    case 'f_emision': return fmt(ch.fecha_emision);
    case 'f_cobro': return fmt(ch.fecha_cobro);
    case 'importe': return `$${formatMoneda(ch.importe)}`;
    case 'electronico': return ch.es_electronico ? 'Sí' : 'No';
    case 'estado': return ch.estado || 'recibido';
    case 'comprobantes': {
      const ing = ch.comprobante_ingreso?.numero_recibo
        ? `Recibo N° ${ch.comprobante_ingreso.numero_recibo}`
        : 'Recibo';
      const egComp = ch.comprobante_egreso;
      const eg = egComp?.numero != null || egComp?.numero_pago != null || egComp?.numero_pago_cuenta != null
        ? `${egComp.tipo === 'Pago a cuenta' ? 'Pago a cuenta' : 'OP'} N° ${egComp.numero ?? egComp.numero_pago_cuenta ?? egComp.numero_pago}`
        : 'En cartera';
      return `${ing} · ${eg}`;
    }
    default: return '-';
  }
}

export function valorCeldaCartera(ch, colId, formatFecha) {
  return esc(valorCeldaCarteraPlain(ch, colId, formatFecha));
}

export function valorCeldaCarteraExport(ch, colId, formatFecha) {
  return valorCeldaCarteraPlain(ch, colId, formatFecha);
}

function valorCeldaEmitidosPlain(ch, colId, formatFecha) {
  const fmt = formatFecha || ((f) => (f ? formatFechaSistema(f) : '-'));
  switch (colId) {
    case 'fecha': return fmt(ch.recibo_fecha || ch.fecha_emision);
    case 'cliente': return ch.cliente_razon_social || '-';
    case 'numero': return ch.numero || '-';
    case 'f_cobro': return fmt(ch.fecha_cobro);
    case 'importe': return `$${formatMoneda(ch.importe)}`;
    case 'cuenta_banco': {
      if (ch.cuenta_numero && ch.cuenta_banco_nombre) {
        return `${ch.cuenta_numero} (${ch.cuenta_banco_nombre})`;
      }
      return '-';
    }
    case 'electronico': return ch.es_electronico ? 'Sí' : 'No';
    case 'estado': return ch.estado || 'emitido';
    case 'comprobantes': {
      const o = ch.comprobante_origen;
      if (!o) return '-';
      const num = o.numero ?? o.numero_recibo ?? o.numero_pago_cuenta ?? o.numero_pago;
      const numTxt = num != null ? `N° ${num}` : '';
      return [o.tipo, numTxt].filter(Boolean).join(' ');
    }
    default: return '-';
  }
}

export function valorCeldaEmitidos(ch, colId, formatFecha) {
  return esc(valorCeldaEmitidosPlain(ch, colId, formatFecha));
}

export function valorCeldaEmitidosExport(ch, colId, formatFecha) {
  return valorCeldaEmitidosPlain(ch, colId, formatFecha);
}

export function imprimirListadoCarteraCheques({
  cheques,
  columnasDefinicion,
  columnasVisiblesIds,
  formatFecha,
  filtrosHtml,
  logoUrl,
  subtitulo,
}) {
  const columnas = (columnasVisiblesIds || []).map((id) => {
    const def = columnasDefinicion.find((c) => c.id === id);
    return {
      id,
      label: def?.label || id,
      align: id === 'importe' ? 'right' : 'left',
      incluirComprobantes: id === 'comprobantes',
    };
  });

  const totalImporte = (cheques || []).reduce((s, ch) => s + parseFloat(ch.importe || 0), 0);

  imprimirListadoCheques({
    titulo: 'Cartera de cheques',
    subtitulo,
    columnas,
    filas: cheques,
    getValorCelda: (ch, colId) => valorCeldaCartera(ch, colId, formatFecha),
    filtrosHtml,
    totalImporte,
    logoUrl,
  });
}

export function imprimirListadoChequesEmitidos({
  cheques,
  columnasDefinicion,
  columnasVisiblesIds,
  formatFecha,
  filtrosHtml,
  logoUrl,
  subtitulo,
}) {
  const columnas = (columnasVisiblesIds || []).map((id) => {
    const def = columnasDefinicion.find((c) => c.id === id);
    return {
      id,
      label: def?.label || id,
      align: id === 'importe' ? 'right' : 'left',
      incluirComprobantes: id === 'comprobantes',
    };
  });

  const totalImporte = (cheques || []).reduce((s, ch) => s + parseFloat(ch.importe || 0), 0);

  imprimirListadoCheques({
    titulo: 'Cheques emitidos',
    subtitulo,
    columnas,
    filas: cheques,
    getValorCelda: (ch, colId) => valorCeldaEmitidos(ch, colId, formatFecha),
    filtrosHtml,
    totalImporte,
    logoUrl,
  });
}
